<template>
    <side-panel class="user-details">
        <div class="position-sticky title d-flex flex-row justify-content-between p-3">
            <div class="mr-1">
                <router-link class="btn btn-outline-dark border-0 rounded-0"
                             to="/users">
                    <font-awesome-icon icon="arrow-left"></font-awesome-icon>
                </router-link>
            </div>
            <div class="flex-grow-1">
                <h3 class="font-weight-bolder mb-1">
                    {{ name }}
                </h3>
                <p class="text-dark mb-0">
                    <strong>{{ user ? $config.roleMap[role] : "" | capitalize }}</strong>於{{ timeCreated }}加入
                </p>
            </div>
            <div>
                <b-avatar v-b-tooltip.html
                          v-bind:title="name"
                          placement="auto"
                          variant="light"
                          size="3.75rem"
                          v-bind:src="avatar ? `${ $config.apiUrl }/${ $config.apiVersion }/user/avatar/${ avatar }` : ''">
                    <font-awesome-icon class="h-100 w-100 p-3"
                                       icon="user-tie"
                                       v-if="!avatar && role === 'vending-admin'">
                    </font-awesome-icon>
                    <font-awesome-icon class="h-100 w-100 p-3"
                                       icon="user-hard-hat"
                                       v-if="!avatar && role === 'vending-site-manager'">
                    </font-awesome-icon>
                </b-avatar>
            </div>
        </div>

        <div class="row no-gutters pb-3 px-3">
            <div class="col-md-12"
                 v-if="vendorId">
                <label class="font-weight-bold mb-1">
                    <font-awesome-icon v-bind:icon="['fad', 'id-badge']"></font-awesome-icon>  利興系統編號
                </label>
                <p class="content">
                    {{ vendorId }}
                </p>
            </div>

            <div class="col-md-12">
                <label class="font-weight-bold mb-1">
                    <font-awesome-icon v-bind:icon="['fad', 'at']"></font-awesome-icon> 電郵地址
                </label>
                <p class="content">
                    {{ email }}
                </p>
            </div>

            <div class="col-md-6">
                <label class="font-weight-bold mb-1">
                    <font-awesome-icon v-bind:icon="['fad', 'phone']"></font-awesome-icon> 電話號碼
                </label>
                <p class="content">
                    +{{ countryCode }} {{ phone }}
                </p>
            </div>

            <div class="col-md-6">
                <template v-if="fax">
                    <label class="font-weight-bold mb-1">
                        <font-awesome-icon v-bind:icon="['fad', 'fax']"></font-awesome-icon> 傳真號碼
                    </label>
                    <p class="content">
                        +{{ countryCode }} {{ fax }}
                    </p>
                </template>
            </div>

            <div class="col-md-12"
                 v-if="address">
                <label class="font-weight-bold mb-1">
                    <font-awesome-icon v-bind:icon="['fad', 'map-marker-alt']"></font-awesome-icon> 地址
                </label>
                <p class="content">
                    {{ address }}
                </p>
            </div>

            <div class="col-md-12"
                 v-if="notes">
                <label class="font-weight-bold mb-1">
                    <font-awesome-icon v-bind:icon="['fad', 'sticky-note']"></font-awesome-icon> 備注
                </label>
                <p class="content">
                    {{ notes }}
                </p>
            </div>
        </div>

        <div class="position-sticky actions p-3 d-flex justify-content-between">
            <button class="btn btn-block btn-warning shadow-sm m-0 mr-1"
                    v-bind:class="{ 'btn-loading': isEditing }"
                    v-on:click="isEditUserModalOpened = true">
                <font-awesome-icon icon="edit"></font-awesome-icon> 編輯用戶
            </button>
            <button class="btn btn-block btn-danger shadow-sm m-0 ml-1"
                    v-bind:class="{ 'btn-loading': isEditing }"
                    v-on:click="isReauthenticateModalOpened = true">
                <font-awesome-icon icon="trash-alt"></font-awesome-icon> 刪除用戶
            </button>
        </div>

        <modal-user is-editing
                    v-if="adminRole === 'vending-admin'"
                    v-bind:user="user"
                    v-model="isEditUserModalOpened">
        </modal-user>

        <modal-re-authenticate v-if="['vending-admin', ].includes(adminRole)"
                               v-bind:email="adminEmail"
                               v-bind:callback="openDeleteUserModal"
                               v-model="isReauthenticateModalOpened">
            <p>You are conducting a sensitive action. Please authenticate yourself. </p>
        </modal-re-authenticate>

        <modal-delete-confirm v-if="['vending-admin', ].includes(adminRole)"
                              v-bind:user="user"
                              v-bind:callback="deleteUser"
                              v-model="isDeleteUserModalOpened">
        </modal-delete-confirm>
    </side-panel>
</template>

<script>
    import {
        formatDistanceToNow,
        parseISO,
    } from "date-fns";

    import {
        zhTW
    } from "date-fns/locale";

    import {
        BAvatar,
        VBTooltip,
    } from "bootstrap-vue";

    import SidePanel from "../SidePanel";
    import ModalUser from "./ModalUser";
    import ModalReAuthenticate from "../modals/ModalReAuthenticate";
    import ModalDeleteConfirm from "../modals/ModalDeleteConfirm";

    export default {
        name: "UserDetails",
        components: {
            BAvatar,
            ModalDeleteConfirm,
            ModalReAuthenticate,
            ModalUser,
            SidePanel,
        },
        directives: {
            "b-tooltip": VBTooltip,
        },
        props: {
            "userId": {
                type: String,
            },
        },
        data () {
            return {
                isLoading: true,
                isEditing: false,

                isReauthenticateModalOpened: false,
                isEditUserModalOpened: false,
                isDeleteUserModalOpened: false,
            };
        },
        computed: {
            adminRole () {
                return this.$store.state.user.user.role;
            },
            adminEmail () {
                return this.$store.state.user.user.email;
            },
            users () {
                return this.$store.getters["users/all"];
            },
            user () {
                const target = this.users
                    .find(
                        user => user._id === this.userId
                    );
                if (target) {
                    return target;
                }
                return null;
            },
            avatar () {
                return this.user && this.user.avatar ? this.user.avatar : "";
            },
            vendorId () {
                return this.user?.vendorId ?? "";
            },
            name () {
                return this.user?.name ?? "";
            },
            role () {
                return this.user?.role ?? "";
            },
            countryCode () {
                return this.user?.countryCode ?? "";
            },
            phone () {
                return this.user?.phone ?? "";
            },
            fax () {
                return this.user?.fax ?? "";
            },
            email () {
                return this.user?.email ?? "";
            },
            notes () {
                return this.user?.notes ?? "";
            },
            timeCreated () {
                if (this.user) {
                    return formatDistanceToNow(
                        parseISO(this.user.createdAt),
                        {
                            "locale": zhTW,
                            "addSuffix": true
                        }
                    );
                }
                return "";
            },
            gender () {
                return this.user && this.user.profile && this.user.profile.gender ?
                    this.user.profile.gender :
                    "";
            },
            verified () {
                return this.user.verified;
            },
        },
        methods: {
            openDeleteUserModal () {
                this.isDeleteUserModalOpened = true;
            },

            async deleteUser () {
                this.isEditing = true;
                try {
                    await this.$store.dispatch(
                        "users/deleteUser",
                        this.user._id
                    );
                } catch (e) {
                    console.error(e);
                    this.isEditing = false;
                    return;
                }
                this.isEditing = false;
                this.$router.push("/users");
            },
        },
    }
</script>

<style lang="scss" scoped>
    .user-details {
        .title {
            top: 0;

            background-color: rgba(255, 255, 255, 1);
            border-top-left-radius: 15px;
            border-top-right-radius: 15px;

            z-index: 3;
        }

        .actions {
            bottom: 0;
            z-index: 3;
        }

        .gender-card {
            &.male {
                background-color: #2766f6;

                .logo {
                    color: #0843c8;
                }
            }

            &.female {
                background-color: #fc363b;

                .logo {
                    color: #e20309;
                }
            }
        }

        .age-group-card {
            background-color: rgba(255, 132, 51, 1);

            .logo {
                color: rgba(230, 91, 0, 1);
            }
        }

        .intro-card {
            background-color: rgba(106, 110, 115, 1);

            .logo {
                color: rgba(69, 72, 75, 1);
            }

            .introduction {
                white-space: pre-line;
            }
        }

        .referrer-card {
            background-color: rgba(23, 162, 184, 1);

            .logo {
                color: rgba(15, 102, 116, 1);
            }
        }
    }
</style>
